import React from 'react';
import API from '@API/index';
import { useDispatch, useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import { setSites } from '@store/sites/actions';
import { useSnackbar } from 'notistack';
import Utils from '@utils/index';

/**
 * Hook that will perform an update to Redux Store (Sites) when called.
 * This hook is useful when you want to check for updates on backend and update the App State at the same time.
 * @param companyId ID of the company to perform the update.
 * @param deps Dependencies to trigger the update logic.
 * 
 * @example
 * const Component: React.FC<{}> = () => {
 *  // Updates Sites every time this component mounts.
 *  useUpdateSites("forsight_company");
 * }
 * 
 * const Component: React.FC<{}> = () => {
 *  const [selectedCompanyId, setSelectedCompanyId] = React.useState<string>("forsight_company");
 *  const [selectedTab, setSelectedTab] = React.useState<number>(0);
 * 
 *  // Update Sites every time the selectedTab state changes.
 *  useUpdateSites(selectedCompanyId, [selectedTab])
 * }
 */
function useUpdateSites(companyId?: string, deps?: React.DependencyList) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const sites = useSelector((store: IStoreState) => store.sites.sites);
  const companies = useSelector((store: IStoreState) => store.companies.companies);

  const sitesRef = React.useRef(sites);
  const companiesRef = React.useRef(companies)
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => { sitesRef.current = sites }, [sites]);
  React.useEffect(() => { companiesRef.current = companies }, [companies]);

  const getCompanySites = React.useCallback((companyId: string) => {
    return new Promise<{ companyId: string, sites: Site[] }>((resolve, reject) => {
      API.Site.listSites(companyId)
        .then((sites) => {
          resolve({
            companyId,
            sites
          })
        })
        .catch((e) => {
          reject(e)
        })
    })
  }, [])

  const perform = React.useCallback(() => {
    if (companyId) {
      API.Site.listSites(companyId)
        .then((result: Site[]) => {
          dispatch(setSites(companyId, result));
        })
        .catch((e) => {
          enqueueSnackbar(Utils.Error.getErrorMessage(e), { variant: 'error' })
        })
        .finally(() => {
          setLoading(false);
        })
    } else {
      const fetchWithRetry = async (companyId: string, retries = 3, delayMs = 1000) => {
        for (let attempt = 0; attempt <= retries; attempt++) {
          try {
            return await getCompanySites(companyId);
          } catch (e) {
            if (attempt < retries) await new Promise(res => setTimeout(res, delayMs * Math.pow(2, attempt)));
          }
        }
        return undefined;
      };

      const fetchInBatches = async () => {
        const companies = companiesRef.current;
        const BATCH_SIZE = 5;
        for (let i = 0; i < companies.length; i += BATCH_SIZE) {
          const batch = companies.slice(i, i + BATCH_SIZE);
          const results = await Promise.all(batch.map(el => fetchWithRetry(el.companyId)));
          results.forEach((res) => {
            if (res) dispatch(setSites(res.companyId, res.sites))
          })
        }
      };

      fetchInBatches()
        .catch((e) => {
          enqueueSnackbar(Utils.Error.getErrorMessage(e), { variant: 'error' })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [companyId, dispatch, enqueueSnackbar, getCompanySites])

  React.useEffect(perform, [...(deps || []), companyId, perform])

  return loading;
}

export default useUpdateSites;